/**
 * Switches: Document Intake (PROD-3204) in the practice app.
 *  - `intake`      the patient chart's Documents inbox, upload and review page.
 *  - `intake-desk` the lab desk: reports queue, saved report, Amend, and the review page from the desk.
 *
 * Two gates, both must be on:
 *  1. `VITE_DOCUMENT_INTAKE` is the build-time kill switch (same name as scrips-practitioner-react).
 *     Vite inlines it, so a change needs a rebuild. dev-web.yml sets "true"; prod-web.yml omits it,
 *     so no `?x=` address can turn intake on in a prod build. One flag covers both switches.
 *  2. The browser opts in with `?x=intake` (remembered; `?x=-intake` opts out). `intake-desk` is ON by
 *     default since 23 Oct (Andrew): the Order Requests upload goes through intake wherever the flag is
 *     built; `?x=-intake-desk` is a remembered opt-out. See `src/shared/lib/practice-switches.ts`.
 *
 * Clinic services Document Intake (`useDocumentIntakeEnabled`) is a separate per-organization gate
 * that the call sites still check. Plain functions, not hooks: callers combine them with `||`.
 */
import { isPracticeSwitchOn } from '@/shared/lib/practice-switches'

import { coerceFlag } from './use-clinic-edit-rail-enabled'

function documentIntakeBuilt(): boolean {
  return coerceFlag(import.meta.env.VITE_DOCUMENT_INTAKE)
}

/** Whether this build and browser show Document Intake in the patient chart (`?x=intake`). */
export function isIntakeOn(): boolean {
  return documentIntakeBuilt() && isPracticeSwitchOn('intake')
}

/** Whether the Documents inbox rows carry the type picker (PROD-3339, `?x=intake-type`, on top of `?x=intake`). */
export function isIntakeTypePickerOn(): boolean {
  return isIntakeOn() && isPracticeSwitchOn('intake-type')
}

/** Whether this build and browser show the Document Intake lab desk (on by default; `?x=-intake-desk` turns it off). */
export function isIntakeDeskOn(): boolean {
  return documentIntakeBuilt() && isPracticeSwitchOn('intake-desk')
}
